import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  asyncActions,
  clearErrors,
  toggleRethrow,
} from '../../actions/general';

import UserRecord from '../../records/user';
import VerificationInput from 'react-verification-input';
import { useAuth } from '../../hooks/useAuth';

export default function UserTokenVerification({ 
  children, // any inherited DOM
  onAfterAuth, // function to call after authentication
  expired, // local storage expiration boolean
  match, 
  onLoading, // FUNCTION action creator callback to set loading state
  isLoading, // VAR loading state
  onLockout, // function to call if user is locked out
  onNotBorrower, // function to call if user is not borrower type
  onSaveToken, // callback to save token locally
  twofactor
}) {
  const dispatch = useDispatch();
  const { verifyToken } = useAuth();

  const [token, setToken] = useState('');
  const [error, setError] = useState(false);

  const checkToken = async (val) => {
    const res = await verifyToken(
      match.params.type,
      match.params.contact,
      val
    )();
    return res;
  };

  const apiCalls = (apiAttempts, expired, verifiedUser, verifiedToken) => {
    dispatch(onLoading(true));
    dispatch(
      asyncActions(
        onSaveToken(verifiedToken),
        expired && clearErrors()
      )
    )
      .then(() => dispatch(onAfterAuth(twofactor, verifiedUser, verifiedToken)))
      .catch((err) => {
        // In some cases, like coming back into a started session, we may not
        // have a token in local storage yet, if so first set the auth'd users
        // token and try again.
        if (err.status === 401 && apiAttempts < 2) {
          dispatch(clearErrors());
          toggleRethrow();
          apiAttempts++;

          // now retry with a saved token
          dispatch(onSaveToken(verifiedToken));
          apiCalls(apiAttempts, expired, verifiedUser, verifiedToken);
        }
      });
  };

  const save = (verified) => {
    const verifiedUser = new UserRecord(verified.user);
    let apiAttempts = 1;

    dispatch(toggleRethrow());
    apiCalls(apiAttempts, expired, verifiedUser, verified.token);
  };

  useEffect(() => {
    if (token.length === 6) {
      const tokenParts = token.split('');
      let tokenValid = true;

      tokenParts.forEach((char) => {
        if (char === undefined || char === null || char === '') {
          tokenValid = false;
        }
      });

      if (tokenValid) submitToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitToken = async (givenToken) => {
    dispatch(onLoading(true));
    const verified = await checkToken(givenToken);

    if (verified.locked) {
      // IF USER LOCKED OUT
      onLockout();
      dispatch(onLoading(false));
    } else if (verified.user_is_not_borrower) {
      // IF USER IS NON-BORROWER
      onNotBorrower();
    } else if (verified.authorized) {
      save(verified);
    } else {
      setToken('');
      setError(true);
      dispatch(onLoading(false));
    }
  };

  const verificationInputChanged = (e) => {
    setToken(e.target.value);
    setError(false);
  };

  return (
    <div role="form">
      {isLoading && children}
      {!isLoading && <VerificationInput
        debug={false}
        length={6}
        validChars="0-9"
        placeholder="0"
        autoFocus={true}
        removeDefaultStyles
        classNames={{
          container: 'tokens',
          character: 'input',
          characterInactive: 'token-inactive',
          characterSelected: 'token-active',
        }}
        value={token}
        inputProps={{
          onChange: verificationInputChanged,
          type: "tel",
          className: 'is-sr-only'
        }}
      />}

      {!isLoading && error && (
        <div className="notification is-danger has-text-centered mt-6">
          Invalid token
        </div>
      )}
    </div>
  );
}
